// Agulha fora do estado do React: ela anda 60x por segundo, e passar isso por
// setState re-renderizaria a timeline inteira a cada quadro. Quem precisa (agulha,
// timecode, rolagem automática) assina e escreve direto no DOM — o mesmo desenho
// do rafLoop do edvid.
import { useEffect, useRef, useState } from 'react';

export interface Playhead {
  get(): number;
  set(t: number): void;
  subscribe(fn: (t: number) => void): () => void;
  playing: boolean;
}

export function createPlayhead(): Playhead {
  let t = 0;
  const subs = new Set<(t: number) => void>();
  return {
    playing: false,
    get: () => t,
    set(v: number) {
      if (v === t) return;
      t = v;
      subs.forEach(fn => fn(v));
    },
    subscribe(fn) {
      subs.add(fn);
      return () => { subs.delete(fn); };
    },
  };
}

export function usePlayhead(): Playhead {
  const ref = useRef<Playhead | null>(null);
  if (!ref.current) ref.current = createPlayhead();
  return ref.current;
}

/** Valor da agulha como estado, com passo mínimo (para painéis que mostram "o que está sob a agulha"). */
export function usePlayheadValue(playhead: Playhead, step = 0.1): number {
  const [v, setV] = useState(() => playhead.get());
  useEffect(() => playhead.subscribe(t => {
    setV(prev => (Math.abs(prev - t) >= step ? t : prev));
  }), [playhead, step]);
  return v;
}

/** Controle mínimo de mídia comum ao <video> da aba Corte e ao Player do Remotion da aba Final. */
export interface MediaController {
  getTime(): number;
  seek(t: number): void;
  play(): void;
  pause(): void;
  isPaused(): boolean;
  setMuted(m: boolean): void;
  isMuted(): boolean;
}

/** rAF: publica o tempo da mídia (convertido para o tempo da timeline) na agulha. */
export function usePlayheadLoop(
  controller: MediaController | null,
  toTimeline: (mediaT: number) => number,
  playhead: Playhead,
) {
  const toTimelineRef = useRef(toTimeline);
  toTimelineRef.current = toTimeline;
  useEffect(() => {
    if (!controller) return;
    let raf = 0;
    const loop = () => {
      playhead.playing = !controller.isPaused();
      playhead.set(toTimelineRef.current(controller.getTime()));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [controller, playhead]);
}

export function videoController(video: HTMLVideoElement): MediaController {
  return {
    getTime: () => video.currentTime || 0,
    seek: t => { video.currentTime = Math.max(0, t); },
    play: () => { void video.play().catch(() => undefined); },
    pause: () => video.pause(),
    isPaused: () => video.paused || video.ended,
    setMuted: m => { video.muted = m; },
    isMuted: () => video.muted,
  };
}
